/* eslint-disable @typescript-eslint/no-explicit-any */
import { Helmet } from 'react-helmet';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Form, Statistic, Tooltip } from 'antd';

import { PageProps } from '@/types/PageProps';

import InfiniteTable, { ColumnsType } from '@/components/_common/InfiniteTable';
import DefaultRouteTabs from '@/components/_common/DefaultRouteTabs';
import GroupingForm from '@/components/Registry/BillToPay/Grouping/GroupingForm';
import { useAccountPayableListGroup } from '@/core/domain/financial/AccountPayable/AccountPayable.hooks';
import {
  AccountPayableGroupingFormValues,
  AccountPayableGroupingRequest,
  AccountPayableGroupingResponse,
} from '@/core/domain/financial/AccountPayable/AccountPayable.types';
import { currencyFormatter, quantityFormatter } from '@/utils/number';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { useCallback, useEffect, useState } from 'react';
import GenerateGroupingModal from '@/components/Registry/BillToPay/Grouping/GenerateGroupingModal';
import { useDebounce, useDisclosure } from '@/hooks';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import { convertParams, usePersistSearchParams } from '@/utils/queryParams';

function GroupingsBillToPayPage({ route }: PageProps) {
  const intl = useIntl();
  const [form] = Form.useForm<AccountPayableGroupingFormValues>();

  const [queryParams, setQueryParams] = useState<AccountPayableGroupingRequest>(
    {
      order: 'DESC',
      ...convertParams<AccountPayableGroupingRequest>({
        companyId: 'number',
        creditor: 'string',
        employeeId: 'number',
        supplierId: 'number',
        supplier: 'string',
      }),
    },
  );

  const [modalConfirmGrouping, modalConfirmGroupingAction] = useDisclosure();

  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);

  const queryDebounce = useDebounce(queryParams, 500);
  usePersistSearchParams(queryDebounce);

  const { data, isFetching, fetchNextPage, hasNextPage } =
    useAccountPayableListGroup(queryDebounce);

  const accountPayableGroupings = flatInfiniteData(data);

  const handleSetParams = async () => {
    const values = await form.validateFields();

    setQueryParams({ ...values });
  };

  const columns: ColumnsType<AccountPayableGroupingResponse> = [
    {
      dataIndex: 'code',
      title: intl.formatMessage({
        id: 'groupingsAccountPayable.list.header.code',
      }),
    },
    {
      dataIndex: 'description',
      title: intl.formatMessage({
        id: 'groupingsAccountPayable.list.header.description',
      }),
    },
    {
      dataIndex: 'installmentNumber',
      title: intl.formatMessage({
        id: 'groupingsAccountPayable.list.header.installments',
      }),
    },
    {
      dataIndex: 'totalValue',
      title: intl.formatMessage({
        id: 'groupingsAccountPayable.list.header.amount',
      }),
      render: (value) => currencyFormatter(value),
    },
  ];

  const getTotalValue = useCallback(
    () =>
      accountPayableGroupings.reduce(
        (acc, item) =>
          selectedRowKeys.includes(item.accountId)
            ? item.totalValue + acc
            : acc,
        0,
      ),
    [selectedRowKeys],
  );

  const handleCancelAfterSubmit = () => {
    modalConfirmGroupingAction.close();
    setSelectedRowKeys([]);
  };

  useEffect(() => {
    if (queryParams) {
      form.setFieldsValue(queryParams);
    }
  }, []);

  return (
    <>
      <DefaultRouteTabs
        route={route.parent}
        persistSearchParams={false}
        permissions={route.permissions}
        extra={
          <SpaceForButton>
            <Tooltip
              title={
                (selectedRowKeys?.length ?? 0) < 2
                  ? 'Selecione mais de um contas a pagar para gerar o agrupamento'
                  : undefined
              }
            >
              <Button
                block
                type="primary"
                onClick={modalConfirmGroupingAction.open}
                disabled={
                  (selectedRowKeys?.length ?? 0) < 2 ||
                  !hasPermissions([
                    Authorities.FINANCIAL_ACCOUNT_PAYABLE_GROUP_ACCOUNTS_PAYABLE,
                  ])
                }
              >
                <FormattedMessage id="groupingsAccountPayable.generate.clusters" />
              </Button>
            </Tooltip>
          </SpaceForButton>
        }
      >
        <Helmet title={intl.formatMessage({ id: route.name })} />

        <GroupingForm form={form} onCallback={handleSetParams} />

        <InfiniteTable<AccountPayableGroupingResponse>
          rowSelection={{
            onChange: (keys) => setSelectedRowKeys(keys as number[]),
          }}
          columns={columns}
          dataSource={accountPayableGroupings}
          loading={isFetching}
          rowKey={(row) => row.accountId}
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
          footer={() => (
            <Statistic
              style={{ textAlign: 'end' }}
              valueStyle={{ color: 'rgb(160, 206, 51)' }}
              title="Valor de agrupamento"
              value={`R$ ${quantityFormatter(getTotalValue())}`}
            />
          )}
        />
      </DefaultRouteTabs>

      <GenerateGroupingModal
        open={modalConfirmGrouping}
        onCancel={modalConfirmGroupingAction.close}
        data={{ installmentIds: selectedRowKeys, totalValue: getTotalValue() }}
        onCancelInSubmit={handleCancelAfterSubmit}
      />
    </>
  );
}

export default GroupingsBillToPayPage;
